import { memo, useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { useSnackbar } from 'notistack';
import Stack from '@mui/material/Stack';
import { styled } from '@mui/material/styles';

import { IconAlertCircle as ErrorIcon } from '@/components/icon/tabler/IconAlertCircle';
import { IconCircleCheck as CheckCircleIcon } from '@/components/icon/tabler/IconCircleCheck';
import { BrandSymbol } from '@/components/Loaders';
import Button from '@/components/Button';
import AuroraBackdrop from '@/components/AuroraBackdrop';
import AuthCardContainer from '@/domain/auth/components/AuthCardContainer';
import { useActivateAccount, useResendAccountActivation } from '@/api/hooks';
import { ScreenRoutes } from '@/types/router';
import { getUserMessage, normalizeApiError } from '@/utils/error';
import { green } from '@/theme/themePrimitives';
import { useTranslate } from '@/state/i18n';

const ActivateAccountContainer = styled(Stack)(({ theme }) => ({
  minHeight: '100dvh',
  padding: theme.spacing(2),
  [theme.breakpoints.up('sm')]: {
    padding: theme.spacing(4),
  },
  '&::before': {
    content: '""',
    display: 'block',
    position: 'absolute',
    zIndex: -1,
    inset: 0,
  },
}));

const ActivateAccount = () => {
  const t = useTranslate('domain.auth.ActivateAccount');
  const { enqueueSnackbar } = useSnackbar();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const token = searchParams.get('token');

  const { mutate: activateAccount, isPending, error, isSuccess } = useActivateAccount();
  const {
    mutate: resendActivation,
    isPending: isResending,
    isSuccess: resendSucceeded,
  } = useResendAccountActivation();

  useEffect(() => {
    if (!token) {
      return;
    }

    activateAccount(
      { token },
      {
        onSuccess: () => {
          enqueueSnackbar(t('activated'), { variant: 'success' });
        },
      },
    );
  }, [token, activateAccount, enqueueSnackbar, navigate, t]);

  const handleResend = () => {
    if (!token) return;
    resendActivation(
      { token },
      {
        onSuccess: () => enqueueSnackbar(t('requestReceived'), { variant: 'success' }),
        onError: (resendError) =>
          enqueueSnackbar(getUserMessage(resendError, t('resendError')), { variant: 'error' }),
      },
    );
  };

  const activationErrorStatus = error ? normalizeApiError(error).status : undefined;
  const canResend = Boolean(token && error && activationErrorStatus === 400);
  const canRetryActivation = Boolean(token && error && activationErrorStatus !== 400);
  const handleRetryActivation = () => {
    if (!token) return;
    activateAccount(
      { token },
      {
        onSuccess: () => {
          enqueueSnackbar(t('activated'), { variant: 'success' });
        },
      },
    );
  };

  const Icon = useMemo(() => {
    if (isPending) {
      return <BrandSymbol size={64} animation="print" />;
    }
    if (resendSucceeded) {
      return <CheckCircleIcon sx={{ fontSize: 64, color: green[500] }} />;
    }
    if (error || !token) {
      return <ErrorIcon sx={{ fontSize: 64, color: 'error.main' }} />;
    }
    return <CheckCircleIcon sx={{ fontSize: 64, color: green[500] }} />;
  }, [error, isPending, resendSucceeded, token]);

  const title = useMemo(() => {
    if (isPending) {
      return t('activating');
    }
    if (resendSucceeded) {
      return t('requestReceivedTitle');
    }
    if (!token || activationErrorStatus === 400) {
      return t('invalidLink');
    }
    if (error) return t('activationFailed');
    return t('activatedTitle');
  }, [activationErrorStatus, error, isPending, resendSucceeded, token, t]);

  const description = useMemo(() => {
    if (isPending) {
      return t('activatingHint');
    }
    if (resendSucceeded) {
      return t('resendHint');
    }
    if (error || !token) {
      return getUserMessage(error, t('invalidLinkHint'));
    }
    return t('activatedHint');
  }, [error, isPending, resendSucceeded, token, t]);

  const [timer, setTimer] = useState(10);

  // Counts down to the sign-in redirect; the interval is cleared on unmount (bodyprint-web leaks it).
  useEffect(() => {
    if (!isSuccess) return;
    const interval = setInterval(() => setTimer((seconds) => Math.max(seconds - 1, 0)), 1000);
    return () => clearInterval(interval);
  }, [isSuccess]);

  useEffect(() => {
    if (timer !== 0) return;
    navigate(ScreenRoutes.SIGNIN);
  }, [timer, navigate]);

  return (
    <ActivateAccountContainer direction="column" justifyContent="space-between">
      <AuroraBackdrop />
      <AuthCardContainer>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, alignItems: 'center' }}>
          <Stack direction="column" gap={1} alignItems="center">
            {Icon}
            <Typography variant="h4" component="h1" sx={{ textAlign: 'center' }}>
              {title}
            </Typography>
          </Stack>
          <Typography variant="body1" sx={{ textAlign: 'center' }}>
            {description}
          </Typography>
          {timer > 0 && isSuccess && (
            <Typography variant="caption" sx={{ textAlign: 'center', maxWidth: 300 }}>
              {t('redirecting', { count: timer })}
            </Typography>
          )}
          {(error || !token) && !resendSucceeded ? (
            canResend ? (
              <Button
                sx={{ mt: 3 }}
                variant="contained"
                fullWidth
                loading={isResending}
                disabled={isResending}
                onClick={handleResend}
              >
                {t('resendActivation')}
              </Button>
            ) : canRetryActivation ? (
              <Button
                sx={{ mt: 3 }}
                variant="contained"
                fullWidth
                loading={isPending}
                disabled={isPending}
                onClick={handleRetryActivation}
              >
                {t('retryActivation')}
              </Button>
            ) : (
              <Button
                sx={{ mt: 3 }}
                variant="contained"
                fullWidth
                onClick={() => navigate(ScreenRoutes.SIGNUP)}
              >
                {t('signUpAgain')}
              </Button>
            )
          ) : resendSucceeded ? (
            <Button sx={{ mt: 3 }} component="a" href="mailto:" variant="contained" fullWidth>
              {t('openMyEmail')}
            </Button>
          ) : null}
          <Button
            sx={{ mt: isSuccess ? 3 : 1 }}
            variant={isSuccess ? 'contained' : 'text'}
            fullWidth
            onClick={() => navigate(ScreenRoutes.SIGNIN)}
          >
            {t('backToSignIn')}
          </Button>
        </Box>
      </AuthCardContainer>
    </ActivateAccountContainer>
  );
};

export default memo(ActivateAccount);
